<script>
import { orderBy } from 'lodash'
import baseMixin from '@/mixins/baseMixin'
import DataPoint from '@/models/general/dataPoint'
import DonutGraph from '@/components/general/donut-graph.vue'
import StatisticsBreakdownSummaryDataPoint from './statistics-breakdown-summary-data-point.vue'

export default {
  name: 'StatisticsBreakdownCard',
  components: {
    StatisticsBreakdownSummaryDataPoint,
    DonutGraph,
  },
  mixins: [baseMixin],
  inheritAttrs: false,
  props: {
    dataPoints: {
      type: Array,
      default: () => [],
      validator: (list) => list.every((e) => e instanceof DataPoint),
    },
    loading: {
      type: Boolean,
    },
    transparent: {
      type: Boolean,
    },
    graphOptions: {
      type: Object,
      default: () => {},
    },
    /**
     * Setting this property will enable to number categories and the donut chart
     * segments to be clickable and act as a filter for external data
     */
    filter: {
      type: Boolean,
    },
  },
  data() {
    return {
      selection: [],
    }
  },
  computed: {
    orderedDataPoints() {
      return orderBy(this.dataPoints, ['order'], ['asc'])
    },
    listOfSummaryDataPoints() {
      return this.orderedDataPoints.filter((dataPoint) => dataPoint.showSummary)
    },
    listOfGraphDataPoints() {
      return this.orderedDataPoints.filter((dataPoint) => dataPoint.showOnGraph)
    },
    hasGraphData() {
      return this.listOfGraphDataPoints.length > 0
    },
    graphLabels() {
      return this.listOfGraphDataPoints.map((dataPoint) => dataPoint.title)
    },
    graphData() {
      return this.listOfGraphDataPoints.map((dataPoint) => dataPoint.value)
    },
    isFlat() {
      return this.$attrs?.flat
    },
    compSegmentColors() {
      return this.orderedDataPoints.map((dataPoint) => dataPoint.color)
    },
  },
  watch: {
    selection: {
      handler() {
        this.$emit('filterChanged', this.selection)
      },
    },
  },
  methods: {
    /**
     * @param {{ label: string, data: number, segmentIndex: number }} payload
     */
    toggleSelectionBasedOnChartSegmentClicked(payload) {
      const selectionKey =
        this.orderedDataPoints[payload.segmentIndex]?.key || ''
      const alreadySelected = this.selection.find((key) => key === selectionKey)

      if (alreadySelected) {
        this.selection = this.selection.filter((key) => key !== selectionKey)
      } else {
        this.selection.push(selectionKey)
      }
    },
    /**
     * @param {{ label: string, data: number, segmentIndex: number }} payload
     */
    handleChartClicked(payload) {
      this.toggleSelectionBasedOnChartSegmentClicked(payload)
    },
  },
}
</script>

<template>
  <v-card
    :loading="loading"
    v-bind="$attrs"
    :style="{ backgroundColor: transparent ? '#00000000' : '' }"
    :class="['rounded-xl h-100 pb-2', isFlat ? '' : 'custom-elevation']"
  >
    <v-card-title>
      <slot name="header"></slot>
    </v-card-title>
    <v-card-text>
      <v-row class="d-flex align-center">
        <v-col cols="12" md="9">
          <slot
            name="summary"
            :dataPoints="orderedDataPoints"
            :listOfSummaryDataPoints="listOfSummaryDataPoints"
          >
            <v-item-group v-model="selection" multiple>
              <v-row>
                <v-col
                  v-for="dataPoint of listOfSummaryDataPoints"
                  :key="dataPoint.key"
                  cols="12"
                  md="4"
                >
                  <v-item
                    v-slot="{ active, toggle }"
                    :value="dataPoint.key"
                    :disabled="!filter"
                  >
                    <v-card
                      :class="[
                        !filter ? 'cursor-default' : 'stat-filter-card',
                        active ? 'stat-filter-card--selected blue--text' : '',
                        'pa-0 ma-0 transparent',
                      ]"
                      flat
                      tile
                      @click="toggle"
                    >
                      <StatisticsBreakdownSummaryDataPoint
                        :data-point="dataPoint"
                        :filter="filter"
                      />
                    </v-card>
                  </v-item>
                </v-col>
              </v-row>
            </v-item-group>
          </slot>
        </v-col>
        <v-col cols="12" md="3">
          <slot name="graph">
            <div class="d-flex justify-end">
              <DonutGraph
                :data="graphData"
                :labels="graphLabels"
                :segment-colors="compSegmentColors"
                :height="70"
                :width="160"
                :options="graphOptions"
                @onChartClick="handleChartClicked"
              />
            </div>
          </slot>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions class="py-0">
      <slot name="footer"></slot>
    </v-card-actions>
  </v-card>
</template>

<style lang="scss" scoped>
.stat-filter-card {
  border-color: transparent !important;
  border-bottom: 2px solid rgba(0, 0, 0, 0) !important;

  &.stat-filter-card--selected {
    border-color: #00acef !important;
  }
}
</style>
